// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.1
// updated  : 2026-06-20
// protected: NO  -> v1.1: grafik trend penjualan dihapus (keputusan Owner 2026-06-20).
//            KPI penjualan hari/minggu/bulan tetap dipertahankan.
// =============================================================================
// Dashboard role-based: Sales Metrics (Owner/Kacab/Store), Notifikasi Tugas, SOP.
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CalendarDays, CalendarRange, ListTodo, BookOpen, ChevronRight, BellRing, PackageX, Boxes,
  DatabaseBackup, ShieldCheck, ShieldAlert, Bell, CheckCircle2, Clock, Gift,
} from "lucide-react";
import { useV2Auth } from "@/v2/V2AuthContext";
import { useV2LiveRefresh } from "@/v2/V2RealtimeContext";
import { sopForRole } from "@/v2/sopContent";
import { StockAlertActionButton } from "@/v2/components/StockAlertAction";

const SALES_ROLES = ["OWNER", "KACAB_SBY", "KACAB_SMD", "STAFF_STORE_AREMA", "STAFF_STORE_BORNEO"];
// Fase 6A: role yang punya menu Notifikasi Stok (badge dashboard mengarah ke halaman itu).
const ALERT_ROLES = ["OWNER", "KACAB_SBY", "KACAB_SMD", "ADMIN_GUDANG_SBY", "ADMIN_GUDANG_SMD"];
// Bayar Nanti: role yang melihat badge piutang (Staff Store lunasi; Owner/Kacab pantau region).
const RECEIVABLE_ROLES = ["OWNER", "KACAB_SBY", "KACAB_SMD", "STAFF_STORE_AREMA", "STAFF_STORE_BORNEO"];
// SLICE S2: Notifikasi Compliment KASIR STORE -> HANYA Owner & Kepala Cabang (Staff Store = pelaku, tidak lihat).
const COMPLIMENT_NOTIF_ROLES = ["OWNER", "KACAB_SBY", "KACAB_SMD"];
const STORE_LABEL = { STORE_AREMA: "Store Arema", STORE_BORNEO: "Store Borneo" };
// SLICE S2: SELALU tampilkan dua angka (hari ini + bulan ini). Tiga keadaan (keputusan Owner).
const complimentText = (today, month) => {
  if (today > 0) return `${today} unit hari ini · ${month} unit bulan ini`;
  if (month > 0) return `Tidak ada hari ini · ${month} unit bulan ini`;
  return "Belum ada compliment bulan ini";
};
const rupiah = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

const MetricCard = ({ icon: Icon, label, value, sub, testid }) => (
  <Card className="p-5 rounded-2xl" data-testid={testid}>
    <div className="flex items-center gap-2 text-muted-foreground text-sm"><Icon className="w-4 h-4" />{label}</div>
    <p className="text-2xl font-semibold mt-2 tabular-nums">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </Card>
);

const STATUS_TONE = {
  success: "default", failed: "destructive", partial: "secondary", skipped: "outline",
};
const fmtTime = (s) => {
  if (!s) return "-";
  try { return new Date(s).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
};
const BackupTile = ({ label, data, testid }) => (
  <div className="rounded-xl border p-3" data-testid={testid}>
    <p className="text-xs text-muted-foreground">{label}</p>
    {data ? (
      <div className="mt-1 space-y-1">
        <Badge variant={STATUS_TONE[data.status] || "outline"} data-testid={`${testid}-status`}>{data.status}{data.dry_run ? " · dry-run" : ""}</Badge>
        <p className="text-xs text-muted-foreground">{fmtTime(data.finished_at || data.created_at)}</p>
        {data.note && <p className="text-[11px] text-muted-foreground">{data.note}</p>}
      </div>
    ) : (
      <p className="text-sm text-muted-foreground mt-1" data-testid={`${testid}-empty`}>Belum ada riwayat backup</p>
    )}
  </div>
);

export default function Dashboard() {
  const { me, api, resetActiveInProd } = useV2Auth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [stock, setStock] = useState(null);
  const [alertSum, setAlertSum] = useState(null);
  const [backup, setBackup] = useState(null);
  const [arSum, setArSum] = useState(null);
  const [complimentNotif, setComplimentNotif] = useState(null);
  const [complimentError, setComplimentError] = useState(false);
  const [loading, setLoading] = useState(true);
  const role = me?.role;
  const canSeeSales = SALES_ROLES.includes(role);
  const canSeeAlerts = ALERT_ROLES.includes(role);
  const canSeeReceivables = RECEIVABLE_ROLES.includes(role);
  const canSeeComplimentNotif = COMPLIMENT_NOTIF_ROLES.includes(role);
  const isOwner = role === "OWNER";
  const sop = sopForRole(role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [api("/api/v2/dashboard/tasks"), api("/api/v2/dashboard/stock-alerts")];
      if (canSeeSales) reqs.push(api("/api/v2/dashboard/sales-metrics"));
      const [tRes, sRes, mRes] = await Promise.all(reqs);
      if (tRes.ok) setTasks(await tRes.json());
      if (sRes && sRes.ok) setStock(await sRes.json());
      if (mRes && mRes.ok) setMetrics(await mRes.json());
      if (canSeeAlerts) {
        try {
          const aRes = await api("/api/v2/stock/alerts");
          if (aRes.ok) setAlertSum(await aRes.json());
        } catch (e) { /* skip-safe: badge alert opsional */ }
      }
      if (isOwner) {
        try {
          const bRes = await api("/api/v2/backup/status");
          if (bRes.ok) setBackup(await bRes.json());
        } catch (e) { /* skip-safe: backup status opsional */ }
      }
      if (canSeeReceivables) {
        try {
          const rRes = await api("/api/v2/receivables/summary");
          if (rRes.ok) setArSum(await rRes.json());
        } catch (e) { /* skip-safe: badge piutang opsional */ }
      }
      if (canSeeComplimentNotif) {
        // H.1: bedakan loading / sukses / GAGAL secara eksplisit + CATAT kegagalan
        // (jangan telan diam — kegagalan yang disamarkan sbg "0 compliment" itu menyesatkan).
        try {
          const cRes = await api("/api/v2/dashboard/compliment-notif");
          if (cRes.ok) {
            setComplimentNotif(await cRes.json());
            setComplimentError(false);
          } else {
            setComplimentError(true);
            console.error(`[Dashboard] Gagal memuat notifikasi Compliment: HTTP ${cRes.status}`);
          }
        } catch (e) {
          setComplimentError(true);
          console.error("[Dashboard] Error memuat notifikasi Compliment:", e);
        }
      }
    } finally { setLoading(false); }
  }, [api, canSeeSales, canSeeAlerts, canSeeReceivables, canSeeComplimentNotif, isOwner]);

  useEffect(() => { load(); }, [load]);
  useV2LiveRefresh(load);  // FASE 7B: refresh otomatis saat ada mutasi relevan (region-aware).

  return (
    <div className="space-y-6" data-testid="v2-dashboard">
      {isOwner && resetActiveInProd && (
        <Card className="p-4 border-destructive bg-destructive/10 flex items-start gap-3" data-testid="v2-reset-prod-warning">
          <ShieldAlert className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="font-semibold text-destructive">PERHATIAN: Reset Data masih AKTIF di PRODUCTION.</p>
            <p className="text-sm text-muted-foreground">
              Risiko data terhapus tak sengaja. Setelah UAT selesai, segera nonaktifkan (RESET_DATA_ENABLED=false).
            </p>
          </div>
        </Card>
      )}
      <div>
        <h2 className="text-2xl font-semibold">Halo, {me?.name || me?.email}</h2>
        <p className="text-sm text-muted-foreground">
          Ringkasan operasional <Badge variant="outline">{role}</Badge> · region <Badge variant="secondary">{me?.region || "GLOBAL"}</Badge>
        </p>
      </div>

      {/* Fase 6A: Badge Notifikasi Stok Rendah (JUAL-only, region-aware) — klik ke halaman detail */}
      {canSeeAlerts && (
        <button
          type="button"
          onClick={() => navigate("/stock-alerts")}
          data-testid="v2-stock-alert-badge"
          aria-label="Buka Notifikasi Stok Rendah"
          className="w-full text-left"
        >
          <Card
            className={`p-4 rounded-2xl flex items-center justify-between gap-3 transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring ${
              alertSum && alertSum.total > 0 ? "border-[hsl(var(--warning))]/70" : ""
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className={`relative shrink-0 rounded-xl p-2 ${alertSum && alertSum.total > 0 ? "bg-[hsl(var(--warning))]/10" : "bg-muted"}`}>
                <Bell className={`w-5 h-5 ${alertSum && alertSum.total > 0 ? "text-[hsl(var(--warning))]" : "text-muted-foreground"}`} />
                {alertSum && alertSum.total > 0 && (
                  <span
                    data-testid="v2-stock-alert-count"
                    className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold flex items-center justify-center tabular-nums"
                  >
                    {alertSum.total > 99 ? "99+" : alertSum.total}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm">Notifikasi Stok Rendah</p>
                {loading && !alertSum ? (
                  <p className="text-xs text-muted-foreground">Memuat…</p>
                ) : alertSum && alertSum.total > 0 ? (
                  <p className="text-xs text-muted-foreground truncate">
                    {alertSum.total} item JUAL di bawah ambang · region {alertSum.region}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[hsl(var(--success))]" /> Semua stok JUAL aman
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {alertSum && alertSum.critical > 0 && (
                <Badge variant="destructive" data-testid="v2-stock-alert-critical">{alertSum.critical} Habis</Badge>
              )}
              {alertSum && alertSum.low > 0 && (
                <Badge variant="default" data-testid="v2-stock-alert-low">{alertSum.low} Rendah</Badge>
              )}
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </Card>
        </button>
      )}

      {/* SLICE S2: Kartu Notifikasi Compliment Store (jalur KASIR) — Owner/Kacab. Klik -> Rekap Compliment.
          Nol unit tetap tampil (keadaan tenang) supaya Kacab tahu fiturnya ada. */}
      {canSeeComplimentNotif && (
        <button type="button" onClick={() => navigate("/laporan?tab=compliment")}
          data-testid="v2-compliment-notif-badge" aria-label="Buka Rekap Compliment Store"
          className="w-full text-left">
          <Card className="p-4 rounded-2xl flex items-center justify-between gap-3 transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring">
            <div className="flex items-center gap-3 min-w-0">
              <div className="relative shrink-0 rounded-xl p-2 bg-muted">
                <Gift className="w-5 h-5 text-muted-foreground" />
                {complimentNotif && complimentNotif.today_units > 0 && (
                  <span data-testid="v2-compliment-notif-count"
                    className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center justify-center tabular-nums">
                    {complimentNotif.today_units > 99 ? "99+" : complimentNotif.today_units}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm">Compliment Store</p>
                {loading && !complimentNotif && !complimentError ? (
                  <p className="text-xs text-muted-foreground">Memuat…</p>
                ) : complimentError ? (
                  <p className="text-xs text-destructive font-medium" data-testid="v2-compliment-notif-error">
                    Gagal memuat data compliment. Muat ulang halaman untuk mencoba lagi.
                  </p>
                ) : complimentNotif && complimentNotif.is_owner ? (
                  <div className="space-y-0.5">
                    {complimentNotif.stores.map((s) => (
                      <p key={s.store} className="text-xs text-muted-foreground truncate"
                        data-testid={`v2-compliment-notif-store-${s.store}`}>
                        <span className="text-foreground font-medium">{STORE_LABEL[s.store] || s.store}</span>: {complimentText(s.today_units, s.month_units)}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground truncate" data-testid="v2-compliment-notif-summary">
                    {complimentText(complimentNotif?.today_units || 0, complimentNotif?.month_units || 0)}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {complimentNotif && complimentNotif.today_units > 0 && (
                <Badge variant="warning" data-testid="v2-compliment-notif-today">{complimentNotif.today_units} hari ini</Badge>
              )}
              {complimentNotif && complimentNotif.month_units > 0 && (
                <Badge variant="secondary" data-testid="v2-compliment-notif-month">{complimentNotif.month_units} bulan ini</Badge>
              )}
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </Card>
        </button>
      )}

      {/* Bayar Nanti: Badge Piutang Belum Lunas (region-aware) — klik ke halaman Rekap */}
      {canSeeReceivables && (
        <button type="button" onClick={() => navigate("/piutang")}
          data-testid="v2-receivable-badge" aria-label="Buka Rekap Piutang Bayar Nanti"
          className="w-full text-left">
          <Card className={`p-4 rounded-2xl flex items-center justify-between gap-3 transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring ${arSum && arSum.count > 0 ? "border-[hsl(var(--warning))]/70" : ""}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className={`relative shrink-0 rounded-xl p-2 ${arSum && arSum.count > 0 ? "bg-[hsl(var(--warning))]/10" : "bg-muted"}`}>
                <Clock className={`w-5 h-5 ${arSum && arSum.count > 0 ? "text-[hsl(var(--warning))]" : "text-muted-foreground"}`} />
                {arSum && arSum.count > 0 && (
                  <span data-testid="v2-receivable-count"
                    className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))] text-[10px] font-semibold flex items-center justify-center tabular-nums">
                    {arSum.count > 99 ? "99+" : arSum.count}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm">Piutang Bayar Nanti</p>
                {loading && !arSum ? (
                  <p className="text-xs text-muted-foreground">Memuat…</p>
                ) : arSum && arSum.count > 0 ? (
                  <p className="text-xs text-muted-foreground truncate" data-testid="v2-receivable-summary">
                    {arSum.count} transaksi belum lunas · {rupiah(arSum.total_value)}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[hsl(var(--success))]" /> Tidak ada piutang belum lunas
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {arSum && arSum.count > 0 && (
                <Badge variant="warning" data-testid="v2-receivable-total">{rupiah(arSum.total_value)}</Badge>
              )}
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </Card>
        </button>
      )}

      {/* Sales Metrics */}
      {canSeeSales && (
        loading && !metrics ? (
          <div className="grid md:grid-cols-3 gap-4"><Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-28" /></div>
        ) : metrics && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <MetricCard icon={CalendarDays} label="Penjualan Hari Ini" value={rupiah(metrics.today.total)} sub={`${metrics.today.count} transaksi`} testid="v2-metric-today" />
              <MetricCard icon={CalendarRange} label="Penjualan Minggu Ini" value={rupiah(metrics.week.total)} sub={`${metrics.week.count} transaksi`} testid="v2-metric-week" />
              <MetricCard icon={CalendarRange} label="Penjualan Bulan Ini" value={rupiah(metrics.month.total)} sub={`${metrics.month.count} transaksi`} testid="v2-metric-month" />
            </div>
          </div>
        )
      )}

      {/* Status Backup GCS — OWNER only (UAT GCS Backup) */}
      {isOwner && (
        <Card className="p-5 rounded-2xl" data-testid="v2-backup-status-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <DatabaseBackup className="w-4 h-4 text-primary" />
              <h3 className="font-semibold">Status Backup</h3>
            </div>
            {backup?.config?.gcs_enabled
              ? <Badge variant="default" data-testid="v2-backup-badge"><ShieldCheck className="w-3 h-3 mr-1" />Aktif</Badge>
              : <Badge variant="outline" data-testid="v2-backup-badge"><ShieldAlert className="w-3 h-3 mr-1" />Belum aktif</Badge>}
          </div>
          {loading && !backup ? <Skeleton className="h-20" /> : !backup ? (
            <p className="text-sm text-muted-foreground py-3 text-center">Status backup tidak tersedia.</p>
          ) : (
            <div className="space-y-3">
              {!backup.config?.gcs_enabled && (
                <p className="text-xs text-[hsl(var(--warning))]" data-testid="v2-backup-inactive-msg">
                  {backup.message || "Backup GCS belum aktif"} — bucket: {backup.config?.gcs_bucket || "-"}.
                </p>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <BackupTile label="Backup Database terakhir" data={backup.last_database} testid="v2-backup-db" />
                <BackupTile label="Backup File (R2) terakhir" data={backup.last_r2_files} testid="v2-backup-r2" />
              </div>
            </div>
          )}
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Tasks */}
        <Card className="p-5 rounded-2xl" data-testid="v2-tasks">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><ListTodo className="w-4 h-4 text-primary" /><h3 className="font-semibold">Notifikasi Tugas</h3></div>
            {tasks?.read_only && <Badge variant="outline">read-only</Badge>}
          </div>
          {loading && !tasks ? <Skeleton className="h-20" /> : (
            (tasks?.tasks?.length ? (
              <div className="space-y-2">
                {tasks.tasks.map((t) => (
                  <button key={t.key} onClick={() => navigate(`/${t.module}`)} data-testid={`v2-task-${t.key}`}
                    className="w-full flex items-center justify-between gap-3 p-3 rounded-xl border hover:bg-muted text-left transition-colors">
                    <div className="flex items-center gap-3">
                      <BellRing className={`w-4 h-4 ${t.severity === "warning" ? "text-[hsl(var(--warning))]" : "text-primary"}`} />
                      <span className="text-sm">{t.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={t.severity === "warning" ? "default" : "secondary"}>{t.count}</Badge>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </button>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground py-4 text-center">Tidak ada tugas tertunda. 🎉</p>)
          )}
        </Card>

        {/* SOP */}
        <Card className="p-5 rounded-2xl" data-testid="v2-sop-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-primary" /><h3 className="font-semibold">{sop.title}</h3></div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/sop")}>Selengkapnya</Button>
          </div>
          <ul className="space-y-2">
            {sop.points.map((p, i) => (
              <li key={i} className="flex gap-2 text-sm text-muted-foreground"><span className="text-primary">•</span>{p}</li>
            ))}
          </ul>
        </Card>
      </div>

      {/* SLICE S-A3: Notifikasi Stok — SATU sumber dgn "Notifikasi Stok Rendah" (JUAL-only). */}
      <Card className="p-5 rounded-2xl" data-testid="v2-stock-alerts">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2"><Boxes className="w-4 h-4 text-primary" /><h3 className="font-semibold">{stock?.title || "Notifikasi Stok"}</h3></div>
          {stock && (
            <div className="flex items-center gap-2">
              <Badge variant="destructive" data-testid="v2-stock-habis">{stock.habis || 0} Habis</Badge>
              <Badge variant="default" data-testid="v2-stock-hampir">{stock.hampir_habis || 0} Hampir Habis</Badge>
            </div>
          )}
        </div>
        {loading && !stock ? <Skeleton className="h-20" /> : (
          stock?.items?.length ? (
            <>
            <div className="hidden md:block overflow-x-auto max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground sticky top-0 bg-card">
                  <tr className="text-left border-b">
                    <th className="py-2 pr-3">Merchandize</th><th className="pr-3">Jenis Stok</th>
                    <th className="pr-3">Lokasi</th><th className="pr-3">Region</th>
                    <th className="pr-3 text-right">Stok</th><th className="pr-3">Status</th>
                    {stock?.action && <th className="pr-3 text-right">Aksi</th>}
                  </tr>
                </thead>
                <tbody>
                  {stock.items.map((s, i) => (
                    <tr key={i} className="border-b last:border-0" data-testid="v2-stock-row">
                      <td className="py-2 pr-3 font-medium">{s.merchandize}<span className="text-[10px] text-muted-foreground"> · {s.club}</span></td>
                      <td className="pr-3">{s.jenis_stok}</td>
                      <td className="pr-3">{s.location}</td>
                      <td className="pr-3">{s.region}</td>
                      <td className="pr-3 text-right tabular-nums font-semibold">{s.qty}</td>
                      <td className="pr-3">
                        <Badge variant={s.qty === 0 ? "destructive" : "default"} className="gap-1">
                          {s.qty === 0 && <PackageX className="w-3 h-3" />}{s.status}
                        </Badge>
                      </td>
                      {stock?.action && (
                        <td className="pr-3 text-right">
                          <StockAlertActionButton action={stock.action} productId={s.product_id} />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile: card list stok */}
            <div className="md:hidden max-h-96 overflow-y-auto space-y-3" data-testid="v2-stock-cards">
              {stock.items.map((s, i) => (
                <div key={i} className="rounded-xl border p-3 space-y-1.5" data-testid="v2-stock-row-m">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-medium min-w-0">{s.merchandize}<span className="text-[10px] text-muted-foreground"> · {s.club}</span></span>
                    <div className="flex items-center gap-1 shrink-0">
                      {stock?.action && <StockAlertActionButton action={stock.action} productId={s.product_id} />}
                      <Badge variant={s.qty === 0 ? "destructive" : "default"} className="gap-1">
                        {s.qty === 0 && <PackageX className="w-3 h-3" />}{s.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                    <span>Jenis: <b className="text-foreground">{s.jenis_stok}</b></span>
                    <span>Lokasi: <b className="text-foreground">{s.location}</b></span>
                    <span>Region: <b className="text-foreground">{s.region}</b></span>
                    <span>Stok: <b className="text-foreground tabular-nums">{s.qty}</b></span>
                  </div>
                </div>
              ))}
            </div>
            </>
          ) : <p className="text-sm text-muted-foreground py-4 text-center">Semua Merchandize stok aman (di atas {stock?.max || 20}). 👍</p>
        )}
      </Card>
    </div>
  );
}
